import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import type { LiftCurvePoint } from '@/hooks/usePerformance';

interface PerformanceLiftCurveProps {
  points: LiftCurvePoint[];
  windowDays: number;
  /**
   * One Date per significant product (its uploaded_to_gmc_at). Drives the
   * x-axis date labels and the date banner under the chart. If all dates
   * are equal (typical "one upload wave" case) we show the actual calendar
   * dates on the axis; otherwise we fall back to the relative-days labels
   * and list the upload dates in the banner.
   */
  uploadDatesByProduct?: Date[];
  /**
   * True, wenn die Liste hinter `uploadDatesByProduct` den Seiten-Deckel von
   * `get_optimization_impact_v2` (`p_limit`) erreicht hat. Die RPC hinter der
   * Kurve aggregiert serverseitig ueber ALLE signifikanten Produkte, die Liste
   * ist gedeckelt — dann darf am Kopf keine nackte Zahl stehen, sondern eine
   * untere Schranke. Eine exakte Gesamtzahl gibt es hier nicht zu holen:
   * `get_lift_curve` liefert nur Produktzahlen je Tag, keine Feed-Summe.
   */
  sampleSizeCapped?: boolean;
}

interface SmoothedPoint {
  relative_day: number;
  ctr_pct: number | null;
}

/**
 * Rolling 7-day moving average using click-weighted pooling.
 *
 * For each day d, we sum clicks and impressions across [d-3, d+3] (clamped to bounds)
 * and compute CTR = Σclicks/Σimpressions. This is more honest than a plain mean of
 * daily CTRs because it gives high-impression days proportional weight.
 *
 * IMPORTANT: the window never crosses the upload boundary (day 0). The pre-side
 * (relative_day < 0) only averages over pre-days; the post-side (relative_day >= 0)
 * only averages over post-days. Without this guard, the pre-line near day -1 would
 * be pulled upward by the high post-CTR values bleeding into its lookahead window —
 * producing a misleading "pre rising into upload" artefact even when the true
 * pre-CTR was flat. Bug fixed 2026-05-18 after live user feedback on Cake Boutique
 * 14d view, where the pre-line shot up from 1.2% to 2.7% just before day 0.
 */
function computeMovingAverage(points: LiftCurvePoint[], windowSize = 7): SmoothedPoint[] {
  const half = Math.floor(windowSize / 2);
  return points.map((p, idx) => {
    const start = Math.max(0, idx - half);
    const end = Math.min(points.length, idx + half + 1);
    const isPre = p.relative_day < 0;
    const slice = points.slice(start, end).filter(x =>
      isPre ? x.relative_day < 0 : x.relative_day >= 0
    );
    const totalClicks = slice.reduce((s, x) => s + x.clicks, 0);
    const totalImp = slice.reduce((s, x) => s + x.impressions, 0);
    return {
      relative_day: p.relative_day,
      ctr_pct: totalImp > 0 ? (totalClicks / totalImp) * 100 : null,
    };
  });
}

export function PerformanceLiftCurve({
  points,
  windowDays,
  uploadDatesByProduct = [],
  sampleSizeCapped = false,
}: PerformanceLiftCurveProps) {
  const { t, i18n } = useTranslation('performance');
  const dateLocale = i18n.language === 'de' ? 'de-DE' : 'en-US';

  // Trigger animation shortly after mount.
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 80);
    return () => clearTimeout(timer);
  }, []);

  // Smart date labels: only show real dates on the axis when ALL significant
  // products were uploaded on the same calendar day. Otherwise different
  // products have different absolute dates for the same relative day, so the
  // axis cannot show a single honest date — we fall back to relative days and
  // list the actual upload dates in the banner below the chart.
  const uniqueUploadYmds = Array.from(
    new Set(uploadDatesByProduct.map(d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`))
  ).sort();
  const isUniformUpload = uniqueUploadYmds.length === 1;
  const fmtShort = (d: Date) => d.toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit' });

  // Latest day we may show: today − 2 (the GMC reporting lag — anything later
  // would falsely suggest a data point exists that simply hasn't synced yet).
  const today2 = new Date();
  today2.setDate(today2.getDate() - 2);

  let leftAxisLabel = `−${windowDays}d`;
  let centerAxisLabel = `⚡ ${t('liftCurve.uploadMarker', 'Upload')}`;
  let rightAxisLabel = `+${windowDays - 1}d`;
  let bannerText = '';

  if (isUniformUpload) {
    const uploadDate = new Date(`${uniqueUploadYmds[0]}T00:00:00`);
    const preStart = new Date(uploadDate);
    preStart.setDate(preStart.getDate() - windowDays);
    const postEndRaw = new Date(uploadDate);
    postEndRaw.setDate(postEndRaw.getDate() + windowDays - 1);
    const postEnd = postEndRaw > today2 ? today2 : postEndRaw;

    leftAxisLabel = fmtShort(preStart);
    centerAxisLabel = `⚡ ${fmtShort(uploadDate)}`;
    rightAxisLabel = fmtShort(postEnd);

    bannerText = t('liftCurve.uploadBanner', '📅 {{leftDate}} to {{rightDate}} · Upload {{uploadDate}}', {
      leftDate: fmtShort(preStart),
      rightDate: fmtShort(postEnd),
      uploadDate: fmtShort(uploadDate),
    });
  } else if (uniqueUploadYmds.length > 1) {
    const uploadCounts = uniqueUploadYmds.map(ymd => {
      const count = uploadDatesByProduct.filter(d => {
        const dYmd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        return dYmd === ymd;
      }).length;
      return `${fmtShort(new Date(`${ymd}T00:00:00`))} (${count})`;
    });
    bannerText = t('liftCurve.mixedUploads', '📅 Uploads: {{uploads}} · data through {{dataUntil}}', {
      uploads: uploadCounts.join(', '),
      dataUntil: fmtShort(today2),
    });
  }

  // Bericht 2.7: die Stichprobengroesse der Kurve sichtbar machen. Der Chart
  // ist handgebautes SVG ohne Tooltip-Layer, ein Per-Punkt-Tooltip waere ein
  // Chart-Umbau — also steht die n der signifikanten Produkte im Kurven-Header.
  //
  // Zwei Quellen, beide untere Schranken, deshalb das Maximum:
  //  - uploadDatesByProduct: genau ein Datum je signifikantem Produkt, aber aus
  //    der auf `p_limit` gedeckelten Liste von `get_optimization_impact_v2`.
  //  - product_count je Datenpunkt: serverseitig ueber ALLE signifikanten
  //    Produkte gezaehlt, aber nur die eines einzelnen Tages (Produkte ohne
  //    Daten an diesem Tag fehlen).
  //
  // `get_lift_curve` gibt keine Feed-Gesamtzahl zurueck, es gibt hier also
  // keine exakte Zahl zu holen. Erreicht die Liste den Deckel oder traegt die
  // Tageszaehlung die Zahl, ist n nachweislich nur eine untere Schranke und
  // wird als solche beschriftet. Eine nackte Zahl waere an der Stelle, deren
  // ganzer Zweck Messgenauigkeit ist, still falsch.
  const clientSampleSize = uploadDatesByProduct.length;
  const serverSampleSize = points.reduce((max, p) => Math.max(max, p.product_count ?? 0), 0);
  const sampleSize = Math.max(clientSampleSize, serverSampleSize);
  const sampleSizeIsLowerBound = sampleSizeCapped || serverSampleSize > clientSampleSize;

  const smoothed = computeMovingAverage(points, 7);
  const validPoints = smoothed.filter(p => p.ctr_pct !== null);

  if (validPoints.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-primary" />
            {t('liftCurve.title', 'CTR vor / nach Upload (signifikante Produkte)')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">
            {t('liftCurve.noData', 'Keine signifikanten Optimierungen zum Anzeigen.')}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Split into pre (< 0) and post (>= 0) phases.
  const prePoints = smoothed.filter(p => p.relative_day < 0 && p.ctr_pct !== null);
  const postPoints = smoothed.filter(p => p.relative_day >= 0 && p.ctr_pct !== null);

  // Pre/post averages from raw points (not smoothed) so the side-labels are click-honest.
  const preRaw = points.filter(p => p.relative_day < 0);
  const postRaw = points.filter(p => p.relative_day >= 0);
  const sumPreClk = preRaw.reduce((s, p) => s + p.clicks, 0);
  const sumPreImp = preRaw.reduce((s, p) => s + p.impressions, 0);
  const sumPostClk = postRaw.reduce((s, p) => s + p.clicks, 0);
  const sumPostImp = postRaw.reduce((s, p) => s + p.impressions, 0);
  const preAvg = sumPreImp > 0 ? (sumPreClk / sumPreImp) * 100 : null;
  const postAvg = sumPostImp > 0 ? (sumPostClk / sumPostImp) * 100 : null;

  // SVG geometry — viewBox-scaled, responsive via CSS.
  const width = 720;
  const height = 280;
  const padLeft = 50;
  const padRight = 16;
  const padTop = 24;
  const padBottom = 36;
  const chartW = width - padLeft - padRight;
  const chartH = height - padTop - padBottom;

  const maxCtrFromSmoothed = Math.max(...validPoints.map(p => Number(p.ctr_pct)));
  const maxY = Math.max(0.5, Math.ceil(maxCtrFromSmoothed * 1.25 * 10) / 10);

  const minDay = -windowDays;
  const maxDay = windowDays - 1;
  const dayRange = maxDay - minDay || 1;
  const toX = (day: number) => padLeft + ((day - minDay) / dayRange) * chartW;
  const toY = (v: number) => padTop + chartH - (v / maxY) * chartH;

  // Path strings. Pre is a separate grey curve; post is the gradient hero curve.
  const buildPath = (pts: SmoothedPoint[]) =>
    pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${toX(p.relative_day).toFixed(2)},${toY(Number(p.ctr_pct)).toFixed(2)}`).join(' ');

  const prePath = buildPath(prePoints);
  const postPath = buildPath(postPoints);

  // Closed area path for the post-curve fill (down to baseline + back to start).
  const postAreaPath =
    postPoints.length > 0
      ? `${postPath} L ${toX(postPoints[postPoints.length - 1].relative_day).toFixed(2)},${(padTop + chartH).toFixed(2)} L ${toX(postPoints[0].relative_day).toFixed(2)},${(padTop + chartH).toFixed(2)} Z`
      : '';

  const markerX = toX(0);

  // Y-axis ticks — adaptive step based on maxY.
  const yTicks: number[] = [];
  const tickStep = maxY >= 8 ? 2 : maxY >= 4 ? 1 : maxY >= 2 ? 0.5 : maxY >= 1 ? 0.25 : 0.1;
  for (let v = 0; v <= maxY + 0.0001; v += tickStep) {
    yTicks.push(Math.round(v * 100) / 100);
  }

  // Einzeichen-Offset der Nachher-Linie: ein einzelner grosser Wert, kein
  // Strichmuster (Fix-Runde 1) -- die Draw-in-Animation gehoert nur der
  // Nachher-Linie, dem sichtbarsten Moment des Charts (die Ergebniskurve
  // zeichnet sich), waehrend die Vorher-Linie ohne Animation bleibt (Auflage 2,
  // echte Kollision mit ihrem statischen Strichmuster).
  const pathLen = 1000;

  const endPoint = postPoints[postPoints.length - 1];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 flex-wrap">
          <BarChart3 className="h-4 w-4 text-primary" />
          {t('liftCurve.title', 'CTR vor / nach Upload (signifikante Produkte)')}
          <span className="text-xs text-muted-foreground font-normal ml-2">
            {t('liftCurve.windowLabel', 'Fenster {{days}} Tage · 7-Tage Moving Average', { days: windowDays })}
            {sampleSize > 0 && (
              <> · {sampleSizeIsLowerBound
                ? t('liftCurve.tooltipNAtLeast', { count: sampleSize })
                : t('liftCurve.tooltipN', { count: sampleSize })}</>
            )}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="relative">
          <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto">
            {/* D21: die Lift-Kurve zeigt CTR je Tag, eine extern GEMESSENE
                Groesse (nicht unser Urteil wie der Quality Score) -- Farbe ist
                der Datenakzent accent-data, nicht primary. Die Flaechenfuellung
                bleibt ein Alpha-Fade DERSELBEN Farbe (0,15 nach 0), das ist
                nach Regel 1 ausdruecklich erlaubt und kein Marken-Verlauf; die
                Deckkraft sinkt dabei bewusst von 0,25 auf 0,15. Der zweite
                Verlauf `liftAfterLine` (primary->violet fuer den Linienstrich)
                und der Weichzeichner-Filter `liftLineGlow` entfallen ersatzlos:
                eine Linie hat eine Farbe, kein Verlaufsband, und keinen Glow. */}
            <defs>
              <linearGradient id="liftAfterFill" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="hsl(var(--accent-data))" stopOpacity="0.15" />
                <stop offset="100%" stopColor="hsl(var(--accent-data))" stopOpacity="0" />
              </linearGradient>
            </defs>

            {/* Y-axis gridlines + labels */}
            {yTicks.map(v => {
              const y = toY(v);
              return (
                <g key={v}>
                  <line
                    x1={padLeft}
                    y1={y}
                    x2={width - padRight}
                    y2={y}
                    stroke="hsl(var(--border))"
                    strokeWidth="1"
                    strokeDasharray="2 3"
                    opacity="0.5"
                  />
                  <text
                    x={padLeft - 8}
                    y={y + 3}
                    textAnchor="end"
                    fontSize="10"
                    fill="hsl(var(--muted-foreground))"
                  >
                    {tickStep < 1 ? v.toFixed(2).replace(/\.?0+$/, '') : v.toString()}%
                  </text>
                </g>
              );
            })}

            {/* X-axis labels (left edge, center, right edge) */}
            <text x={padLeft} y={height - 12} fontSize="10" fill="hsl(var(--muted-foreground))">
              {leftAxisLabel}
            </text>
            <text
              x={markerX}
              y={height - 12}
              textAnchor="middle"
              fontSize="10"
              fill="hsl(var(--primary))"
              fontWeight="700"
            >
              {centerAxisLabel}
            </text>
            <text
              x={width - padRight}
              y={height - 12}
              textAnchor="end"
              fontSize="10"
              fill="hsl(var(--muted-foreground))"
            >
              {rightAxisLabel}
            </text>

            {/* Vertical upload marker line at day 0 */}
            <line
              x1={markerX}
              y1={padTop}
              x2={markerX}
              y2={padTop + chartH}
              stroke="hsl(var(--primary))"
              strokeWidth="1.5"
              strokeDasharray="4 3"
              opacity={isVisible ? 0.7 : 0}
              style={{ transition: 'opacity 600ms ease-out 300ms' }}
            />

            {/* Pre line: flat grey, gestrichelt als Zweitkodierung (D25): mit
                der Nachher-Linie im Datenakzent liegen die beiden Toene unter
                Protanopie zu nah beieinander, ausserdem traegt Nachher im
                Light Mode ohnehin schon den dunkleren Zwilling accent-data-ink.
                Nur Farbe unterscheidet die beiden Serien dann nicht zuverlaessig.

                Muster ist eines der beiden bereits im Chart vorhandenen
                Strichrhythmen ("2 3" an den Gitterlinien, "4 3" an der
                senkrechten Upload-Markierung) und nicht ein dritter Wert:
                ein drittes Muster stellte drei Strichrhythmen nebeneinander,
                ohne dass eine dritte Rolle dafuer existiert. Gewaehlt ist "4 3":
                bei strokeWidth 2 wirkt "2 3" (Dash kaum laenger als die
                Strichbreite) fast wie eine gepunktete statt einer gestrichelten
                Linie, waehrend "4 3" auch bei dieser Strichbreite klar als
                Strich-Luecke-Rhythmus lesbar bleibt (dasselbe Verhaeltnis wie
                an der 1,5px-Markierungslinie, dort nur groesser skaliert).

                Die Einzeichen-Animation dieser Linie entfaellt bewusst: sie
                belegte `strokeDasharray`/`strokeDashoffset`, genau die
                Eigenschaft, die jetzt den Strichstil traegt (der Test in
                `liftCurveEncoding.test.tsx` trennt MUSTER von OFFSET ueber die
                Werteform: mindestens zwei kleine Werte fuer ein echtes
                Strichmuster gegen genau einen Wert >= 100 fuer den
                Einzeichen-Offset einer Draw-in-Animation -- die Vorher-Linie
                traegt hier das Muster, nicht den Offset). Die
                Vorher-Linie ist die Vergleichsbasis, nicht das Ergebnis --
                sie muss nicht eingezeichnet werden. Die Zweitkodierung ist
                eine Zusicherung an die Lesbarkeit (D25), die Animation nur
                Schmuck; ein simples Opacity-Fade uebernimmt den Einblend-Effekt. */}
            {prePath && (
              <path
                d={prePath}
                data-serie="pre"
                stroke="hsl(var(--muted-foreground))"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="4 3"
                opacity={isVisible ? 1 : 0}
                style={{ transition: 'opacity 600ms ease-out' }}
              />
            )}

            {/* Post area fill */}
            {postAreaPath && (
              <path
                d={postAreaPath}
                fill="url(#liftAfterFill)"
                opacity={isVisible ? 1 : 0}
                style={{ transition: 'opacity 1s ease-out 1.4s' }}
              />
            )}

            {/* Post line main. Der Glow-Duplikatpfad (Weichzeichner-Filter,
                Opazitaet 0,35) faellt ersatzlos weg (D24: kein Glow auf
                Datenlinien). Statt `url(#liftAfterLine)` traegt die Linie die
                Zwillingsregel: ein SVG-Attribut kennt keine Media Query, darum
                laeuft die Theme-Abhaengigkeit ueber die von S2 bereitgestellten
                Tailwind-Klassen. 2,5px sind eine duenne Linie, `#2BB3A9` misst
                darauf im Light Mode nur 2,29:1 auf Sand und faellt unter WCAG
                1.4.11 -- deshalb dort der dunklere Zwilling accent-data-ink. Im
                Dark Mode zeigt der Zwilling heute auf denselben Wert wie
                `--accent-data` (5,88:1, tauglich): `src/tokens.generated.css`
                setzt `--accent-data-ink` im `.dark`-Block auf `176 61% 44%`,
                identisch zu `--accent-data`. Die `dark:`-Klasse ist damit heute
                ein No-op, haelt die Regel aber offen: faellt der Zwilling im
                Dark Mode je auseinander, greift sie automatisch, ohne dass hier
                jemand daran denken muss. `stroke` entfaellt, sonst gewinnt das
                Attribut vor der Klasse.

                Die Draw-in-Animation bleibt hier bewusst (Fix-Runde 1): die
                Nachher-Linie ist der sichtbarste Moment des Charts, die
                Ergebniskurve zeichnet sich. Sie kollidiert nicht mit der
                Zweitkodierung, weil `pathLen=1000` ein einzelner GROSSER Wert
                ist (der Einzeichen-Offset), kein Strichmuster -- der Test
                unterscheidet das explizit (mindestens zwei kleine Werte fuer
                ein Muster gegen genau einen Wert >= 100 fuer einen Offset). */}
            {postPath && (
              <path
                d={postPath}
                data-serie="post"
                className="stroke-accent-data-ink dark:stroke-accent-data"
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray={pathLen}
                strokeDashoffset={isVisible ? 0 : pathLen}
                style={{ transition: 'stroke-dashoffset 1.4s ease-out 1.2s' }}
              />
            )}

            {/* End-point dot. Der aeussere Puls-Glow-Kreis (r="9", Opazitaet
                0,25) faellt weg (D24). Der innere Kreis ist eine Flaeche ab
                rund 3px Radius und darf deshalb den Vollton accent-data
                tragen (anders als die 2,5px-Linie oben); ein 1px-Rahmen in
                Tinte (foreground) grenzt ihn gegen die Kartenflaeche ab. */}
            {endPoint && (
              <circle
                cx={toX(endPoint.relative_day)}
                cy={toY(Number(endPoint.ctr_pct))}
                r="5"
                fill="hsl(var(--accent-data))"
                stroke="hsl(var(--foreground))"
                strokeWidth="1"
                opacity={isVisible ? 1 : 0}
                style={{ transition: 'opacity 400ms ease-out 2.6s' }}
              />
            )}
          </svg>

          {/* Date banner — real calendar dates when uploads are uniform, or list of upload days when mixed */}
          {bannerText && (
            <div className="text-[10px] text-muted-foreground text-center mt-2 font-mono">
              {bannerText}
            </div>
          )}

          {/* Pre / post average labels, same pattern as marketing site.
              Der Nachher-Swatch traegt die Zwillingsregel wie die Kurve selbst
              (dunkler Zwilling im Light Mode, Vollton im Dark Mode). Der
              Vorher-Swatch bekommt denselben Strichstil-Hinweis wie die Linie
              (gestrichelter statt durchgezogener Rand), damit die Legende die
              Zweitkodierung mit abbildet und nicht nur ueber Farbe erklaert.
              Der Beschriftungstext laeuft in `text-foreground`, nicht in der
              Kurvenfarbe: nach den Accent-Regeln darf die Kurvenfarbe nicht
              die Beschriftungsfarbe sein, und `text-primary` waere hier eine
              dritte Farbe ohne Bedeutung. */}
          <div className="flex items-center justify-between mt-3 gap-2 text-xs font-medium">
            <div className="flex items-center gap-2">
              <div className="w-3 h-0 border-t-2 border-dashed border-muted-foreground" />
              <span className="text-muted-foreground">
                Ø {t('liftCurve.before', 'Vorher')}:{' '}
                <span className="font-mono font-semibold">
                  {preAvg !== null ? `${preAvg.toFixed(2)}%` : '—'}
                </span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-foreground">
                Ø {t('liftCurve.after', 'Nachher')}:{' '}
                <span className="font-mono font-bold">
                  {postAvg !== null ? `${postAvg.toFixed(2)}%` : '—'}
                </span>
              </span>
              <div className="w-3 h-0.5 bg-accent-data-ink dark:bg-accent-data" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
